import { ChangeEvent, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { DateTime } from "luxon"

import { useMeContext } from "@oysterhr/identity/MeContext"

import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"

import { Button, Checkbox, LoadingSpinner, Toast, useToast } from "@oysterhr/ui"

import { companyPaymentMethodsPath } from "@oysterhr/router"
import { useAuthorizePaymentMethod } from "../../../hooks/useAuthorizeDirectDebitOnPaymentMethod"
import ConnectPaymentMethodLayout from "../../../layouts/ConnectPaymentMethodLayout"
import { useStripeAchPaymentMethod } from "./hooks/useStripeAchPaymentMethod"
import { PaymentMethodState, PaymentMethodStripeAch } from "@oysterhr/schema"
import { PaymentMethodEnumType } from "~/app/utils/constants"

const AuthorizePaymentMethodPage = () => {
  const toast = useToast()
  const { me } = useMeContext()
  const navigate = useNavigate()
  const [checked, setChecked] = useState(false)
  const { currentAspect, accountAspect: companyId } = useAccountAspect()
  const { t } = useTranslation("company_interface/AuthorizePaymentMethod")
  const { data, loading } = useStripeAchPaymentMethod({ companyId })
  const paymentMethod = data?.company?.paymentMethod as PaymentMethodStripeAch | undefined
  const [authorize, { loading: authorizing }] = useAuthorizePaymentMethod()

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    setChecked(checkedStatus => !checkedStatus)
  }, [])

  useEffect(() => {
    if (
      // When there're is no stripe ACH payment method for this company
      (!loading && paymentMethod?.__typename !== PaymentMethodEnumType.STRIPE_ACH) ||
      // When payment method is already authorized
      paymentMethod?.state === PaymentMethodState.AuthorizedForDirectDebit
    ) {
      return navigate(companyPaymentMethodsPath(companyId))
    }
  }, [paymentMethod, loading, companyId, navigate])

  const handleAuthorize = () => {
    authorize({
      variables: {
        authorized: true,
        paymentMethodId: (paymentMethod as PaymentMethodStripeAch).id,
      },
      onCompleted: data => {
        if (data.authorizeDirectDebitOnPaymentMethod.errors.length) {
          toast({
            render: (
              <Toast variation="error">
                <Toast.Content>
                  {data?.authorizeDirectDebitOnPaymentMethod?.errors.join(", ")}
                </Toast.Content>
              </Toast>
            ),
          })
        } else {
          toast({
            render: (
              <Toast variation="success">
                <Toast.Title>{t("toasts.connected.title")}</Toast.Title>
                <Toast.Content>{t("toasts.connected.description")}</Toast.Content>
              </Toast>
            ),
          })
          navigate(companyPaymentMethodsPath(companyId))
        }
      },
    })
  }

  // If payment method is not loaded yet or it's already authorized, show loading spinner
  if (!paymentMethod || paymentMethod?.state === PaymentMethodState.AuthorizedForDirectDebit) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner data-testid="loading-spinner" />
      </div>
    )
  }

  return (
    <ConnectPaymentMethodLayout title={t("connectPaymentMethod")}>
      <div
        className="relative rounded-lg border-2 border-gray-200 bg-white p-2 text-left md:p-8"
        style={{ width: 600 }}
      >
        <div className="flex h-full justify-center px-4">
          <div className="flex flex-col" style={{ width: "8000" }}>
            <div className="text-2xl font-bold">{t("title")}</div>
            <div>
              <div data-testid="authorization-form-content">
                <p className="my-4 mt-8 whitespace-pre-line text-md">
                  {t("form.0", {
                    companyName: currentAspect.company?.name || "",
                    date: DateTime.now().toISODate(),
                  })}
                </p>
                <p className="my-4 whitespace-pre-line text-md">{t("form.1")}</p>
                <p className="my-4 whitespace-pre-line text-md">
                  <b>{t("form.2.bold")}</b> {t("form.2.content")}
                </p>
                <p className="my-4 whitespace-pre-line text-md">
                  <b>{t("form.3.bold")}</b> {t("form.3.content")}
                </p>
                <p className="my-4 whitespace-pre-line text-md">{t("form.4.title")}</p>
                <ul className="list-disc">
                  <li className="mx-6 mt-3">{t("form.4.list.0")}</li>
                  <li className="mx-6 mt-3">{t("form.4.list.1")}</li>
                </ul>
              </div>
              <div className="my-4">
                <Checkbox
                  label={t("agree", { name: me?.name })}
                  checked={checked}
                  onChange={onChange}
                  name="authorize"
                  id="authorize-checkbox"
                />
              </div>
              <div className="mt-4 flex pb-10">
                <Button
                  data-analytics-name="company_payment_methods-authorize-payment-method"
                  disabled={!checked}
                  isLoading={authorizing}
                  onClick={handleAuthorize}
                >
                  {t("authorize")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ConnectPaymentMethodLayout>
  )
}

export default AuthorizePaymentMethodPage
